import { useState, useEffect, useRef } from 'react';
import { ChevronUp, ChevronDown, Check, Moon, Sun } from 'lucide-react';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { 
  CURRENCIES,
  REGIONS,
  getCurrentRegion, 
  setCurrentRegion,
  getRegionConfig,
  getCurrencyConfig
} from '../config/currency';

// Images
const UAE_FLAG = "https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/9prfeyzu_united-arab-emirates%20%281%29.png";
const DIRHAM_SYMBOL = "https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/wtj4snjt_Dirham%20Currency%20Symbol%20-%20Black.png";

// Currency Icon Component with dark mode support
const CurrencyIcon = ({ code, isDarkMode, size = 'md' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6'
  };
  
  if (code === 'AED') {
    return (
      <img 
        src={DIRHAM_SYMBOL} 
        alt="AED" 
        className={`${sizeClasses[size]} object-contain transition-all`}
        style={{ filter: isDarkMode ? 'invert(1) brightness(2)' : 'none' }}
      />
    );
  }
  
  const symbols = { USD: '$', EUR: '€', CNY: '¥' };
  return (
    <span className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
      {symbols[code]}
    </span>
  );
};

export function RegionSelector({ className = '', compact = false }) {
  const { language, changeLanguage, t } = useLanguage();
  const { isDarkMode, toggleTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [currentRegion, setCurrentRegionState] = useState(null);
  const [currentCurrency, setCurrentCurrencyState] = useState(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const region = getRegionConfig();
    const currency = getCurrencyConfig();
    setCurrentRegionState(region);
    setCurrentCurrencyState(currency);
    
    const handleRegionChange = () => {
      setCurrentRegionState(getRegionConfig());
      setCurrentCurrencyState(getCurrencyConfig());
    };
    
    window.addEventListener('regionChange', handleRegionChange);
    return () => window.removeEventListener('regionChange', handleRegionChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setIsOpen(false);
  };

  const handleCurrencyChange = (currencyCode) => {
    const regionEntry = Object.entries(REGIONS).find(([_, r]) => r.currency === currencyCode && r.available);
    if (regionEntry) {
      setCurrentRegion(regionEntry[0]);
      setCurrentRegionState(REGIONS[regionEntry[0]]);
      setCurrentCurrencyState(CURRENCIES[currencyCode]);
    } else {
      setCurrentCurrencyState(CURRENCIES[currencyCode]);
      localStorage.setItem('cloudgm_currency', currencyCode);
    }
  };

  const handleRegionSelect = (regionCode) => {
    const region = REGIONS[regionCode];
    if (region && region.available) {
      setCurrentRegion(regionCode);
      setCurrentRegionState(region);
      setCurrentCurrencyState(CURRENCIES[region.currency]);
    }
  };

  if (!currentRegion || !currentCurrency) return null;

  const currencies = Object.values(CURRENCIES);
  const availableRegions = Object.values(REGIONS).filter(r => r.available);
  const isRTL = language === 'ar';

  const getCurrencyIcon = (code) => {
    if (code === 'AED') {
      return <img src={DIRHAM_SYMBOL} alt="AED" className="h-4 w-4 object-contain" />;
    }
    const symbols = { USD: '$', EUR: '€', CNY: '¥' };
    return <span className="text-lg font-bold text-[#0F172A]">{symbols[code]}</span>;
  };

  const getFlagImage = (regionCode) => {
    if (regionCode === 'UAE') {
      return <img src={UAE_FLAG} alt="UAE" className="h-5 w-7 object-cover rounded-sm" />;
    }
    const region = REGIONS[regionCode];
    return <span className="text-xl">{region?.flag}</span>;
  };

  const languages = [
    { code: 'en', name: 'English', nameShort: 'EN' },
    { code: 'ar', name: 'العربية', nameShort: 'AR' }
  ];

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Toggle Button - Theme Aware */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center ${compact ? 'px-2 py-1' : 'px-3 py-2'} rounded-xl transition-colors ${
          isDarkMode 
            ? 'bg-white/5 border border-white/10 hover:bg-white/10' 
            : 'bg-gray-100 border border-gray-200 hover:bg-gray-200'
        }`}
        data-testid="region-selector-button"
        style={{ direction: 'ltr' }}
      >
        {/* Theme Icon with separator */}
        <div className={`flex items-center gap-2 pe-2 border-e ${isDarkMode ? 'border-white/10' : 'border-gray-300'}`}>
          {isDarkMode ? (
            <Moon className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-[#1E9BD9]`} />
          ) : (
            <Sun className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} text-[#F59E0B]`} />
          )}
        </div>
        
        {/* Region/Language/Currency */}
        <div className="flex items-center gap-2 ps-2">
          <img src={UAE_FLAG} alt="UAE" className="h-4 w-6 object-cover rounded-sm" />
          <span className={`${compact ? 'text-xs' : 'text-sm'} font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{language.toUpperCase()}</span>
          <CurrencyIcon code="AED" isDarkMode={isDarkMode} size="sm" />
          {isOpen ? (
            <ChevronUp className={`h-3 w-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          ) : (
            <ChevronDown className={`h-3 w-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          )}
        </div>
      </button>

      {/* Dropdown Menu - Theme Aware */}
      {isOpen && (
        <div 
          className={`fixed md:absolute top-auto md:top-full mt-2 w-[calc(100vw-32px)] md:w-auto md:min-w-[280px] max-w-[320px] rounded-xl shadow-2xl z-[100] overflow-hidden ${
            isDarkMode 
              ? 'bg-[#141414] border border-white/10' 
              : 'bg-white border border-gray-200 shadow-lg'
          }`}
          style={{ 
            direction: isRTL ? 'rtl' : 'ltr',
            left: '16px',
            right: '16px'
          }}
        >
          
          {/* Language Section */}
          <div className={`p-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <h3 className={`text-sm font-bold mb-2 whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('region.changeLanguage')}
            </h3>
            
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                data-testid={`lang-option-${lang.code}`}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 last:mb-0 transition-colors ${
                  language === lang.code 
                    ? 'bg-[#1E9BD9]/20' 
                    : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                }`}
              >
                {language === lang.code ? (
                  <Check className="h-4 w-4 text-[#1E9BD9] flex-shrink-0" />
                ) : (
                  <div className="w-4 flex-shrink-0"></div>
                )}
                <span className={`text-sm whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{lang.name} - {lang.nameShort}</span>
              </button>
            ))}
          </div>

          {/* Theme Section */}
          <div className={`p-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <h3 className={`text-sm font-bold mb-3 whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {isRTL ? 'الوضع الليلي' : 'Night Mode'}
            </h3>
            
            {/* Toggle Row with Switch */}
            <div 
              className={`flex items-center justify-between px-2 py-2 rounded-lg cursor-pointer transition-colors ${
                isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'
              }`}
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {/* Right side: Theme icon + Text */}
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  isDarkMode ? 'bg-[#1E9BD9]/20' : 'bg-[#F59E0B]/20'
                }`}>
                  {isDarkMode ? (
                    <Moon className="h-5 w-5 text-[#1E9BD9]" />
                  ) : (
                    <Sun className="h-5 w-5 text-[#F59E0B]" />
                  )}
                </div>
                <span className={`text-sm whitespace-nowrap font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {isRTL ? 'تفعيل الوضع الليلي' : 'Enable Night Mode'}
                </span>
              </div>
              
              {/* Left side: Toggle Switch */}
              <div 
                className={`relative w-12 h-7 rounded-full transition-colors duration-300 ${
                  isDarkMode ? 'bg-[#1E9BD9]' : 'bg-gray-300'
                }`}
              >
                <div 
                  className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform duration-300 ${
                    isDarkMode 
                      ? (isRTL ? 'right-6 translate-x-0' : 'left-1 translate-x-5') 
                      : (isRTL ? 'right-1' : 'left-1')
                  }`}
                />
              </div>
            </div>
          </div>

          {/* Currency Section */}
          <div className={`p-3 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
            <h3 className={`text-sm font-bold mb-2 whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('region.changeCurrency')}
            </h3>
            
            {currencies.map((currency) => (
              <button
                key={currency.code}
                onClick={() => handleCurrencyChange(currency.code)}
                data-testid={`currency-option-${currency.code}`}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 last:mb-0 transition-colors ${
                  currentCurrency.code === currency.code 
                    ? 'bg-[#1E9BD9]/20' 
                    : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                }`}
              >
                {currentCurrency.code === currency.code ? (
                  <Check className="h-4 w-4 text-[#1E9BD9] flex-shrink-0" />
                ) : (
                  <div className="w-4 flex-shrink-0"></div>
                )}
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <CurrencyIcon code={currency.code} isDarkMode={isDarkMode} size="sm" />
                </div>
                <span className={`text-sm whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{isRTL ? currency.name : currency.nameEn} - {currency.code}</span>
              </button>
            ))}
          </div>

          {/* Country Section */}
          <div className="p-3">
            <h3 className={`text-sm font-bold mb-2 whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
              {t('region.country')}
            </h3>
            
            {availableRegions.map((region) => (
              <button
                key={region.code}
                onClick={() => handleRegionSelect(region.code)}
                data-testid={`region-option-${region.code}`}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg mb-1 last:mb-0 transition-colors ${
                  currentRegion.code === region.code 
                    ? 'bg-[#1E9BD9]/20' 
                    : isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'
                }`}
              >
                {currentRegion.code === region.code ? (
                  <Check className="h-4 w-4 text-[#1E9BD9] flex-shrink-0" />
                ) : (
                  <div className="w-4 flex-shrink-0"></div>
                )}
                <div className="flex-shrink-0">
                  {getFlagImage(region.code)}
                </div>
                <span className={`text-sm whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{isRTL ? region.name : region.nameEn}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default RegionSelector;
